import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/shared/Logo';
import { playerCategoryService } from '@/features/game/services/categoryService';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { 
  ChevronDown, ChevronUp, Play, Users, Trophy, 
  Zap, Star, Check, Instagram, Twitter, 
  Youtube, Mail, Phone, MapPin, Heart, Sparkles
} from 'lucide-react';
import type { Category } from '@/features/game/services/categoryService';

import { API_BASE_URL } from '@/lib/api/apiClient';
const API_URL = API_BASE_URL || '';

const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };
const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, prefix = '', suffix = '' }: { 
  end: number; 
  duration?: number; 
  prefix?: string; 
  suffix?: string;
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [isVisible, end, duration]);

  return (
    <div ref={ref} className="text-3xl md:text-4xl font-black text-yellow-300">
      {prefix}{count.toLocaleString()}{suffix}
    </div>
  );
};

export const LandingPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const apiUrl = API_URL;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await playerCategoryService.getCategories();
        setCategories(data.slice(0, 6));
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        setCategories(playerCategoryService.getFallbackCategories().slice(0, 6));
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const pricingPlans = [
    { games: 1, price: '8', currency: 'ر.س', description: 'لعبة واحدة', highlight: false, features: ['لعبة واحدة', '6 فئات', '36 سؤال', 'وسائل مساعدة'] },
    { games: 3, price: '20', currency: 'ر.س', description: 'الأفضل قيمة', highlight: true, features: ['3 ألعاب', '6 فئات لكل لعبة', 'وسائل مساعدة', 'وفر 17%'] },
    { games: 7, price: '35', currency: 'ر.س', description: 'للعائلات', highlight: false, features: ['7 ألعاب', '6 فئات لكل لعبة', 'وسائل مساعدة', 'وفر 38%'] },
  ];

  const faqs = [
    { q: 'ما خطوات بدء لعبة جديدة؟', a: 'سجّل دخولك ثم اختر "إنشاء لعبة". وزّع الحضور على فريقين، واختر ست فئات، سمّ كل فريق، ثم اضغط ابدأ اللعب.' },
    { q: 'هل أستطيع التجربة دون دفع؟', a: 'نعم. كل مستخدم جديد يحصل على جولة مجانية واحدة لتجربة اللعبة.' },
    { q: 'هل الأسئلة تتكرر إذا اخترت نفس الفئات؟', a: 'لا. كل سؤال يظهر مرة واحدة، والجولة المجانية تحتوي على مجموعة ثابتة للتعريف باللعبة فقط.' },
    { q: 'إذا توقفت عن اللعب، هل أستأنف لاحقاً؟', a: 'نعم. أي جولة تبدأها تُحفظ تلقائياً ضمن "ألعابي" ويمكنك متابعتها من أعلى الصفحة.' },
    { q: 'هل تُمنح نقاط جزئية إذا كانت الإجابة ناقصة؟', a: 'لا. تحصل على نقاط السؤال فقط عند الإجابة الكاملة والصحيحة.' },
    { q: 'هل مسموح اختيار أسئلة من فئات الفريق الآخر؟', a: 'نعم. يمكنكم فتح أي خانة من الخانات الست (يمين أو يسار) ضمن الفئات المختارة.' },
    { q: 'ما مدة الإجابة لكل فريق؟', a: 'الفريق صاحب الدور لديه دقيقة واحدة. الفريق الآخر لديه 10 ثوانٍ تبدأ بعد انتهاء إجابة الفريق الأول.' },
  ];

  const getImageUrl = (url: string | undefined) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${apiUrl}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  return (
    <div className="min-h-screen bg-white" style={arabicFont}>
      {/* Global Animations */}
      <style>{`
        @keyframes bounce-vertical {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between">
          <Logo size="hero" animateImage />
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-gray-700 hover:text-green-600 font-medium">المميزات</a>
            <a href="#pricing" className="text-gray-700 hover:text-green-600 font-medium">الأسعار</a>
            <a href="#faq" className="text-gray-700 hover:text-green-600 font-medium">الأسئلة الشائعة</a>
            <Link to="/contact" className="text-gray-700 hover:text-green-600 font-medium">تواصل معنا</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost">دخول</Button></Link>
            <Link to="/register"><Button className="bg-green-600 hover:bg-green-700">سجّل مجاناً</Button></Link>
          </div>
        </div>
      </header>

      {/* Promotional Banner - Ad Space */}
      <div className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 py-3 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMiIgY3g9IjIwIiBjeT0iMjAiIHI9IjIiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center justify-center gap-4 text-center">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-red-600 animate-pulse" />
              <span className="text-red-700 font-bold text-sm md:text-base">
                عرض: ثلاث ألعاب بـ 20 ر.س فقط
              </span>
              <Sparkles className="w-5 h-5 text-red-600 animate-pulse" />
            </div>
            <Link to="/register" className="hidden sm:inline-block">
              <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded-full text-sm font-bold transition-all hover:scale-105 shadow-md">
                اشترك الآن
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="text-center md:text-right space-y-6">
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-5 py-2.5 rounded-full text-sm font-bold">
                <Sparkles className="w-5 h-5 ml-2 text-yellow-300" />
                جولة مجانية لكل حساب جديد
              </div>
              
              <h1 className="text-7xl md:text-8xl lg:text-9xl leading-tight" style={{ ...ruqaaFont, fontWeight: 700, textShadow: '4px 4px 8px rgba(0,0,0,0.4)' }}>
                اعصر مخك
              </h1>
              
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-lg">
                مسابقة معلومات للعائلة والأصدقاء
              </p>
              
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <span className="bg-yellow-400 text-green-800 px-4 py-1.5 rounded-full font-bold text-sm">6 فئات</span>
                <span className="bg-yellow-400 text-green-800 px-4 py-1.5 rounded-full font-bold text-sm">36 سؤال</span>
                <span className="bg-yellow-400 text-green-800 px-4 py-1.5 rounded-full font-bold text-sm">تحدي مثير!</span>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start pt-4">
                <Link to="/register">
                  <button className="bg-white text-green-700 hover:bg-yellow-300 hover:text-green-800 px-10 py-4 text-lg font-bold rounded-2xl shadow-xl transition-all hover:scale-105 inline-flex items-center">
                    <Play className="w-5 h-5 ml-2" />ابدأ اللعب مجاناً
                  </button>
                </Link>
              </div>
              
              <div className="flex gap-8 md:gap-10 justify-center md:justify-start pt-6">
                <div className="text-center"><AnimatedCounter end={10000} prefix="+" /><p className="text-sm text-white/80 font-medium mt-1">لاعب</p></div>
                <div className="text-center"><AnimatedCounter end={50000} prefix="+" /><p className="text-sm text-white/80 font-medium mt-1">سؤال</p></div>
                <div className="text-center"><AnimatedCounter end={100} prefix="+" /><p className="text-sm text-white/80 font-medium mt-1">فئة</p></div>
              </div>
            </div>
            
            <div className="flex justify-center items-center order-first md:order-last">
              <div className="relative">
                <div className="absolute inset-0 bg-white/40 rounded-full blur-3xl scale-125" />
                <img src={apiUrl ? `${apiUrl}/Brain.jpeg` : '/Brain.jpeg'} alt="اعصر مخك" className="relative w-72 h-72 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] object-contain" style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))', animation: 'float 4s ease-in-out infinite' }} />
              </div>
            </div>
          </div>
        </div>
        
        <style>{`@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-20px); } }`}</style>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12" style={ruqaaFont}>لماذا نختار اعصر مخك؟</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'لعب جماعي', desc: 'شارك من تحب في جولات مسلية وتحديات ممتعة' },
              { icon: Trophy, title: 'تنوع المواضيع', desc: 'فئات متعددة تغطي مجالات مختلفة' },
              { icon: Zap, title: 'واجهة بسيطة', desc: 'تصميم واضح وسهل الاستخدام' },
            ].map((feature, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <feature.icon className="w-12 h-12 mx-auto mb-4 text-green-600" />
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-b from-white to-green-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-green-200 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-200 rounded-full blur-3xl opacity-30 translate-x-1/2 translate-y-1/2" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 inline-block animate-wiggle" 
              style={ruqaaFont}
            >
              الفئات
            </h2>
            <p className="text-xl text-gray-600">فئات في شتى المواضيع لاختبار معلوماتك</p>
          </div>
          
          {loading ? (
            <div className="flex justify-center"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-8">
                {categories.map((cat, index) => (
                  <Link 
                    to="/register" 
                    key={cat.id} 
                    className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-2"
                    style={{ 
                      animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both, float 3s ease-in-out ${index * 0.5}s infinite`
                    }}
                  >
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                    
                    {/* Image with overlay */}
                    <div className="relative h-32 overflow-hidden">
                      <img 
                        src={getImageUrl(cat.imageUrl)} 
                        alt={cat.name} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      
                      {/* Sparkle effect */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
                      </div>
                    </div>
                    
                    {/* Category name */}
                    <div className="p-4 text-center bg-gradient-to-b from-white to-green-50">
                      <h3 className="font-bold text-gray-800 group-hover:text-green-600 transition-colors">{cat.name}</h3>
                    </div>
                    
                    {/* Animated border */}
                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-400 rounded-2xl transition-colors duration-300" />
                  </Link>
                ))}
              </div>
              
              {/* More categories banner */}
              <div className="relative mt-12 p-8 bg-gradient-to-r from-green-600 via-green-500 to-green-600 rounded-3xl shadow-xl overflow-hidden">
                {/* Animated background */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,white_1px,transparent_1px)] bg-[length:20px_20px] animate-pulse" />
                </div>
                
                {/* Floating icons */}
                <div className="absolute top-4 left-4 animate-bounce" style={{ animationDelay: '0s' }}>📚</div>
                <div className="absolute top-4 right-4 animate-bounce" style={{ animationDelay: '0.3s' }}>🧠</div>
                <div className="absolute bottom-4 left-8 animate-bounce" style={{ animationDelay: '0.6s' }}>🎮</div>
                <div className="absolute bottom-4 right-8 animate-bounce" style={{ animationDelay: '0.9s' }}>⭐</div>
                
                <div className="relative z-10 text-center text-white">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="text-4xl animate-pulse">✨</span>
                    <h3 className="text-2xl md:text-3xl font-bold" style={ruqaaFont}>
                      وهناك +100 فئة أخرى بانتظارك!
                    </h3>
                    <span className="text-4xl animate-pulse">✨</span>
                  </div>
                  <p className="text-lg text-white/90 mb-6">
                    تاريخ، علوم، رياضة، فن، جغرافيا، دين، أدب، تقنية والمزيد...
                  </p>
                  <Link to="/register">
                    <button className="bg-white text-green-600 hover:bg-yellow-300 hover:text-green-700 px-8 py-3 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2">
                      <span>اكتشف جميع الفئات</span>
                      <span className="animate-bounce">🚀</span>
                    </button>
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Custom animations */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
          }
          @keyframes wiggle {
            0%, 100% { transform: rotate(-3deg); }
            50% { transform: rotate(3deg); }
          }
          .animate-wiggle {
            animation: wiggle 1s ease-in-out infinite;
          }
          @keyframes pulse-logo {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4" style={ruqaaFont}>الأسعار</h2>
          <p className="text-gray-600 text-center mb-12 text-lg">اختر عدد الألعاب المناسب</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto items-center">
            {pricingPlans.map((plan, i) => (
              <div 
                key={i} 
                className={`relative bg-white rounded-3xl p-8 text-center transition-all duration-300 ${
                  plan.highlight 
                    ? 'shadow-2xl scale-110 border-4 border-green-500 z-10' 
                    : 'shadow-lg hover:shadow-xl hover:scale-105'
                }`}
              >
                {/* Highlight badge and effects for middle plan */}
                {plan.highlight && (
                  <>
                    <div className="absolute -inset-1 bg-gradient-to-r from-green-400 via-yellow-400 to-green-400 rounded-3xl blur-sm opacity-75 animate-pulse"></div>
                    <div className="absolute inset-0 bg-white rounded-3xl"></div>
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                      <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg inline-flex items-center gap-2">
                        <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                        الأفضل قيمة
                        <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                      </span>
                    </div>
                    <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                      <div className="absolute top-3 -right-6 bg-red-500 text-white text-xs font-bold py-1 px-8 rotate-45 shadow-md">
                        وفر 17%
                      </div>
                    </div>
                  </>
                )}
                
                <div className={`relative z-10 ${plan.highlight ? 'pt-4' : ''}`}>
                  <p className={`mb-2 font-medium ${plan.highlight ? 'text-green-600' : 'text-gray-500'}`}>
                    {plan.description}
                  </p>
                  <p className={`text-5xl font-black mb-2 ${plan.highlight ? 'text-green-600' : 'text-gray-800'}`}>
                    {plan.price} {(plan as { currency?: string }).currency || 'ر.س'}
                  </p>
                  <p className={`text-lg mb-6 ${plan.highlight ? 'text-green-500 font-bold' : 'text-gray-400'}`}>
                    {plan.games} {plan.games === 1 ? 'لعبة' : 'ألعاب'}
                  </p>
                  
                  <ul className="text-sm space-y-3 mb-8">
                    {plan.features.map((f, j) => (
                      <li key={j} className={`flex items-center gap-2 justify-center ${plan.highlight ? 'text-gray-700' : 'text-gray-600'}`}>
                        <Check className={`w-5 h-5 ${plan.highlight ? 'text-green-500' : 'text-gray-400'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/register">
                    <button className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                      plan.highlight 
                        ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl hover:scale-105' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>
                      {plan.highlight ? '🎉 اشترِ الآن' : 'اشترِ الآن'}
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-4xl font-bold text-center mb-12" style={ruqaaFont}>الأسئلة الشائعة</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-right flex items-center justify-between font-bold" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {faq.q}
                  {openFaq === i ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                {openFaq === i && <div className="px-6 pb-4 text-gray-600">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4" style={ruqaaFont}>اعصر مخك</h3>
              <p className="text-gray-400">لعبة المعلومات الأولى للتجمعات العائلية والأصدقاء</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">روابط سريعة</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white">الرئيسية</Link></li>
                <li><Link to="/login" className="hover:text-white">تسجيل الدخول</Link></li>
                <li><Link to="/register" className="hover:text-white">إنشاء حساب</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">تواصل معنا</h4>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-center gap-2"><Mail className="w-4 h-4" />info@example.com</li>
                <li className="flex items-center gap-2"><Phone className="w-4 h-4" />+249 123 456 789</li>
                <li className="flex items-center gap-2"><MapPin className="w-4 h-4" />السودان</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">تابعنا</h4>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20"><Instagram className="w-5 h-5" /></a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20"><Twitter className="w-5 h-5" /></a>
                <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20"><Youtube className="w-5 h-5" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2026 اعصر مخك - جميع الحقوق محفوظة</p>
            <p className="mt-2 flex items-center justify-center gap-1">صنع بـ <Heart className="w-4 h-4 text-red-500" /> في السودان</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
